import { z } from 'zod';
import { CONTENT_KINDS } from '../domain/access.js';
import { SUBSCRIPTION_PLANS } from '../domain/subscription.js';

/**
 * Admin commands (§48). Each action is its own endpoint rather than a generic
 * PATCH blob, so the intent is visible in the API and in the log (§53).
 */

const noteSchema = z.string().trim().max(300).nullable().optional();

/**
 * Тариф пользователя вручную (docs/PAYMENTS.md, §4.2, §10).
 *
 * `extend` — продлить или выдать на N дней по общим правилам сроков (Лайт при
 * действующем Про сервер отклонит). `set` — «тариф до даты», перекрывает всё
 * прежнее. `cancel` — снять тариф сейчас. Каждое действие — новая строка
 * истории, прежние не переписываются.
 */
export const changeSubscriptionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('extend'),
    plan: z.enum(SUBSCRIPTION_PLANS),
    days: z.number().int().min(1).max(3650),
    note: noteSchema,
    /** Напомнить письмом за 3 дня до окончания. Для ручных выдач можно выключить. */
    notifyExpiry: z.boolean().optional(),
  }),
  z.object({
    action: z.literal('set'),
    plan: z.enum(SUBSCRIPTION_PLANS),
    endsAt: z.string().datetime(),
    note: noteSchema,
    notifyExpiry: z.boolean().optional(),
  }),
  z.object({
    action: z.literal('cancel'),
    note: noteSchema,
  }),
]);
export type ChangeSubscriptionInput = z.infer<typeof changeSubscriptionSchema>;

/** Отзыв конкретной выдачи — с причиной: она остаётся в истории. */
export const revokeEntitlementSchema = z.object({
  reason: z.string().trim().min(1, 'Укажите причину').max(300),
});
export type RevokeEntitlementInput = z.infer<typeof revokeEntitlementSchema>;

/**
 * Применять ли к человеку пробный период (§11).
 *
 * Отдельная команда, а не поле подписки: «купил продукт» и «идёт пробный
 * период» — разные факты, и смешивать их в одном переключателе значило бы
 * снова связать то, что платформа держит порознь (§13).
 */
export const setTrialExemptionSchema = z.object({
  exempt: z.boolean(),
});
export type SetTrialExemptionInput = z.infer<typeof setTrialExemptionSchema>;

export const setGameOwnershipSchema = z.object({
  gameId: z.string().min(1),
  granted: z.boolean(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type SetGameOwnershipInput = z.infer<typeof setGameOwnershipSchema>;

export const setManualAccessSchema = z.object({
  contentKind: z.enum(CONTENT_KINDS),
  contentId: z.string().min(1),
  granted: z.boolean(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type SetManualAccessInput = z.infer<typeof setManualAccessSchema>;
